import _ from 'lodash';
import { widgetStyles, customSchemas, themes as commonThemes } from '@sitebuilder/common';
import { types as coreTypes } from '@sitebuilder/core';

export const getColorPalette = (): string[] => {
    const orderedColors: string[] = [];

    for (let i = 1; i < 11; i++) {
        const paddedIndex = i < 10 ? `0${i}` : i;
        const colorKey = `$color-pie-palette-${paddedIndex}`;
        // eslint-disable-next-line
        orderedColors.push((widgetStyles.themeVars as { [k: string]: string })[colorKey]);
    }

    return orderedColors;
};

export const getThemeBasedColorPalette = (colors: any, colorSpace: any): string[] => {
    const orderedColors: string[] = [];
    const colorSpaceColors = colors
        ? getColorsObj(colors, colorSpace)
        : getColorsObj(commonThemes.defaultPalettes.pieChartPalette, colorSpace);

    Object.keys(colorSpaceColors)
        .sort()
        .forEach((key) => {
            orderedColors.push(colorSpaceColors[key]);
        });

    return orderedColors;
};

export const rebaseGrowthChart = (
    newInput: number,
    seriesData: any,
    nextPoint: any,
    tooltipOptions?: any,
    index?: number,
    yAxisValueOverride?: boolean
) => {
    const diff = newInput / nextPoint.y;
    const mappedData: any = [];
    const tooltipValueOptions = tooltipOptions?.filter((d: any) => d.isValueKey);
    // rebase the values in the series
    seriesData.forEach((point: any) => {
        const y = yAxisValueOverride ? point.y : point.y * diff;
        const newData = { ...point, x: point.x, marker: point.marker, y };
        if (tooltipValueOptions?.length && typeof index === 'number') {
            newData[`${tooltipValueOptions?.[index]?.mappingkey}`] = y;
        }
        mappedData.push(newData);
    });

    return mappedData;
};

export const isLocalStorageAccessible = () => {
    const test = 'test';

    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
};

export const getTextFromHTML = (html: string) => {
    return new DOMParser().parseFromString(html, 'text/html').documentElement.textContent;
};

export const getColorsObj = (colors: any, colorScheme: coreTypes.ColorScheme = 'rgb') => {
    Object.keys(colors)?.forEach((key) => {
        if (
            typeof colors[key] === 'object' &&
            !Array.isArray(colors[key]) &&
            Object.keys(colors[key])?.includes(colorScheme)
        ) {
            colors = {
                ...colors,
                [key]: colors[key][colorScheme],
            } as any;
        } else if (typeof colors[key] === 'string') {
            colors = {
                ...colors,
                [key]: colors[key],
            } as any;
        }
    });
    return colors;
};

export const getCssTheme = (
    theme: coreTypes.Theme | null,
    colorScheme: coreTypes.ColorScheme = 'rgb',
    defaultTypography: coreTypes.Typography,
    componentThemes: coreTypes.ComponentThemes
) => {
    const filteredTheme = getFilteredTheme(theme);
    let colors: any = filteredTheme?.colors;
    let defaultThemeColors: any = filteredTheme?.defaultColors
        ? { ...commonThemes.defaultColors, ...filteredTheme?.defaultColors }
        : { ...commonThemes.defaultColors };

    // get color values from color object for theme object
    if (filteredTheme && colors) {
        colors = getColorsObj(filteredTheme?.colors, colorScheme);
    }

    if (filteredTheme && defaultThemeColors) {
        defaultThemeColors = getColorsObj(defaultThemeColors, colorScheme);
    }

    const typography: coreTypes.Typography = Object.assign(
        {},
        defaultTypography,
        theme?.typography || {}
    );

    const _componentThemes = Object.keys(componentThemes).reduce((obj: any, key: any) => {
        if (filteredTheme?.componentThemes?.[key]) {
            obj[key] = filteredTheme.componentThemes[key];
        } else {
            obj[key] = componentThemes[key];
        }

        return obj;
    }, {});

    return {
        defaultColors: { ...defaultThemeColors },
        colors: { ...colors },
        typography: { ...typography },
        userTypography: theme?.userTypography || {},
        defaultFonts: filteredTheme?.defaultFonts
            ? [...commonThemes.defaultFonts, ...filteredTheme.defaultFonts]
            : [...commonThemes.defaultFonts],
        userFonts: filteredTheme?.userFonts ? [...filteredTheme.userFonts] : [],
        componentThemes: _componentThemes,
    };
};

export const getFilteredTheme = (theme: coreTypes.Theme | null) => {
    const filteredTheme: coreTypes.Theme | null = theme
        ? Object.keys(theme)
              .filter((r: string) => !['id', 'etag'].includes(r))
              .reduce((res: any, key: string) => {
                  res[key] = theme[key as coreTypes.ThemeKeys];
                  return res;
              }, {})
        : null;

    return filteredTheme;
};

export const getCSSStringForTwoLevelObject = (
    object: any,
    type: any,
    colorScheme: string = 'rgb'
) => {
    return object
        ? `${Object.entries(object)
              ?.map(([key, val]: [string, any]) => {
                  const values: string[] = [];
                  val &&
                      Object.entries(val).forEach(([k, v]) => {
                          let value = v;
                          if (
                              typeof value === 'string' &&
                              value?.includes(colorScheme) &&
                              isJSON(value)
                          ) {
                              value = JSON.parse(value)[colorScheme];
                          }
                          const formattedThemeVariable =
                              type === 'componentThemes'
                                  ? `--${key}-${k}:${value}`
                                  : `${key}-${k}:${v}`;
                          v && values.push(formattedThemeVariable);
                      });
                  return values.join(';');
              })
              .join(';')};`
        : '';
};

export const isJSON = (str: string) => {
    try {
        return JSON.parse(str) && !!str;
    } catch (e) {
        return false;
    }
};

export const getCssString = (
    obj?: { [key: string]: string } | null,
    typography?: coreTypes.Typography,
    componentThemes?: coreTypes.ComponentThemes,
    colorSpace?: any
) => {
    let cssString = '';

    const values: string[] = [];
    obj &&
        Object.entries(obj)?.forEach(([k, v]) => {
            if (k === 'color') {
                if (isJSON(v)) {
                    const colorObject = JSON.parse(v);
                    const color = { colorObject };
                    const colorReduce = getColorsObj(color, colorSpace);
                    let key;
                    for (key in colorReduce) {
                        v && values.push(`${k}:${colorReduce[key]}`);
                    }
                } else {
                    v && values.push(`${k}:${v}`);
                }
            } else {
                v && values.push(`${k}:${v}`);
            }
        });
    cssString = `${cssString}${values.length ? `${values.join(';')};` : ''}`;

    cssString = `${cssString}${getCSSStringForTwoLevelObject(
        typography,
        'typography',
        colorSpace
    )}`;

    cssString = `${cssString}${getCSSStringForTwoLevelObject(
        componentThemes,
        'componentThemes',
        colorSpace
    )}`;

    return cssString || '';
};

export const getTypographyCssClasses = (typography: any) => {
    let cssString = '';
    if (typography) {
        Object.keys(typography).forEach((key: any) => {
            const cssProperties: any = [];
            Object.keys(typography[key]).forEach((k: any) => {
                if (typography[key][k]) {
                    cssProperties.push(
                        `${k
                            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                            .toLowerCase()
                            .replace(/ /g, '-')}: ${typography[key][k]}`
                    );
                }
            });
            cssString =
                cssString +
                `.theme-typography${key} { ${cssProperties.length && cssProperties.join(';')};}`;
        });
    }
    return cssString || '';
};

export const getFontFace = (fonts: coreTypes.Fonts[]) => {
    let cssString = '';
    const finalFonts: string[] = [];

    if (fonts) {
        fonts &&
            fonts?.forEach((font: coreTypes.Fonts) => {
                const value = {
                    'font-family': font?.name,
                    src: `url('${font?.src}') format('${font.format}')`,
                    'font-weight': font?.fontWeight,
                    'font-style': font?.fontStyle,
                    'font-display': font?.fontDisplay,
                };
                finalFonts.push(`@font-face{${getCssString(value)}} `);
            });

        cssString = cssString + (finalFonts.length ? finalFonts.join('\n') : '');
    }

    return cssString;
};

export const getTextSchemaProperties = (widgetProperties: any, colorSpace?: any) => {
    const textSchemaProperties: customSchemas.textSchema.TextSchemaProperties = (({
        opacity,
        blendMode,
        decoration,
        fontFamily,
        fontWeight,
        wordSpacing,
        lineHeight,
        textIndent,
        color,
        fontSize,
        horizontalAlignment,
        verticalAlignment,
        shadow,
    }) => ({
        opacity,
        'mix-blend-mode': blendMode,
        'text-decoration': decoration,
        'font-family': fontFamily,
        'font-weight': fontWeight,
        'word-spacing': wordSpacing,
        'line-height': lineHeight,
        'text-indent': textIndent,
        color,
        'font-size': fontSize,
        'text-align': horizontalAlignment,
        'vertical-align': verticalAlignment,
        'text-shadow': shadow,
    }))(widgetProperties || {});

    return getCssString({ ...textSchemaProperties }, undefined, undefined, colorSpace) || '';
};

export const getStyleSchemaProperties = (widgetProperties: any) => {
    const styleSchemaProperties: customSchemas.styleSchema.StyleSchemaProperties = (({
        opacity,
        blendModeStyle,
        backgroundColor,
        borderWidth,
        borderColor,
        borderStyle,
        borderRadius,
        boxShadow,
    }) => ({
        opacity,
        'mix-blend-mode': blendModeStyle,
        'background-color': backgroundColor,
        'border-width': borderWidth,
        'border-color': borderColor,
        'border-style': borderStyle,
        'border-radius': borderRadius,
        'box-shadow': boxShadow,
    }))(widgetProperties || {});

    return getCssString({ ...styleSchemaProperties }) || '';
};

export const getLayoutSchemaProperties = (widgetProperties: any) => {
    const layoutSchemaProperties: customSchemas.layoutSchema.LayoutSchemaProperties = (({
        width,
        height,
        margin,
        padding,
        display,
        flexGrow,
        flexDirection,
        alignItemsHorizontal,
        alignItemsVertical,
    }) => ({
        margin,
        width,
        height,
        padding,
        display,
        'flex-grow': flexGrow,
        'flex-direction': flexDirection,
        'justify-content': alignItemsHorizontal,
        'align-items': alignItemsVertical,
    }))(widgetProperties || {});

    return getCssString({ ...layoutSchemaProperties }) || '';
};

export const getNumberFromString = (str: string) => {
    const regex = /[+-]?\d+(\.\d+)?/g;
    return str?.match(regex)?.map((s) => parseFloat(s))[0];
};

export const RGBToHex = (rgb: string) => {
    // Choose correct separator
    const sep = rgb.indexOf(',') > -1 ? ',' : ' ';
    // Turn "rgb(r,g,b)" into [r,g,b]
    const rgbValue: string[] = rgb.substr(4).split(')')[0].split(sep);

    let r = (+rgbValue[0]).toString(16);
    let g = (+rgbValue[1]).toString(16);
    let b = (+rgbValue[2]).toString(16);

    if (r.length === 1) r = '0' + r;
    if (g.length === 1) g = '0' + g;
    if (b.length === 1) b = '0' + b;

    return '#' + r + g + b;
};

export const convertUTCToLocalDate = (date: Date) => {
    if (!date) {
        return date;
    }
    date = new Date(date);
    date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    return date;
};

export const convertLocalToUTCDate = (date: Date) => {
    if (!date) {
        return date;
    }
    date = new Date(date);
    date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return date;
};

export const applyPageCss = (options: {
    size: string;
    orientation: string;
    width: number;
    height: number;
    margin: { top: number; left: number; bottom: number; right: number };
    padding: { top: number; left: number; bottom: number; right: number };
    bleed: { top: number; left: number; bottom: number; right: number };
}) => {
    let pageSize;
    let pageBleed = '0in';
    let pageMargin = '0in';
    let pagePadding = '0in';

    if (options.size || options.orientation) {
        if (options.size === 'Custom') {
            pageSize = `${options.width}in ${options.height}in`;
        } else {
            pageSize = `${options.size} ${options.orientation}`;
        }
    }

    if (options.margin) {
        const { top, left, bottom, right } = options.margin;
        pageMargin = `${top}in ${right}in ${bottom}in ${left}in`;
    }

    if (options.padding) {
        const { top, left, bottom, right } = options.padding;
        pagePadding = `${top}in ${right}in ${bottom}in ${left}in`;
    }

    if (options.bleed) {
        const { top, left, bottom, right } = options.bleed;
        pageBleed = `${top}in ${right}in ${bottom}in ${left}in`;
    }

    return `size: ${pageSize};
            margin: ${pageMargin};
            padding: ${pagePadding};
            bleed: ${pageBleed};
        `;
};

// Function to check if 'data' has properties other than excludeKey
export const hasOtherProperties = (obj: any, excludeKey: string) => {
    // Iterate over the object's properties
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && key !== excludeKey) {
            return true; // Found a property that is not excludeKey
        }
    }
    return false; // No other properties found
};

export const parseCssToObj = (cssString: string) => {
    const obj: { [k: string]: string } = {};

    cssString.split(';').forEach((rule) => {
        const [property, value] = rule.split(':');
        if (property && value) {
            const prop = property.trim();
            const val = value.trim();
            obj[prop] = val;
        }
    });

    return obj;
};

export async function makeRequest(url: string, method: string, body: BodyInit | null) {
    if (!url) return;

    const headers: HeadersInit = {};
    const options: RequestInit = {
        method,
        headers,
    };

    if (body) {
        headers['Content-Type'] = 'application/json';
        options.body = body;
    }

    return fetch(url, options).then((response: Response) => {
        if (method === 'GET') {
            if (!response.ok) {
                return response.json().then((o: any) => {
                    throw new Error(o.message);
                });
            } else {
                return response.json();
            }
        } else {
            if (!response.ok) {
                return response.text().then((o: any) => {
                    throw new Error(o.message);
                });
            } else {
                return response.text();
            }
        }
    });
}

/**
 * Utility function to get environment-specific API endpoints
 */
export const getAIApiEndpoint = (): string => {
    // In production, use the production endpoint
    if (process.env.NODE_ENV === 'production') {
        return 'https://livedoc.transmissionmedia.ca/api/ai/threads';
    }
    
    // In development or any other environment, use the development endpoint
    return 'https://livedoc-dev.transmissionmedia.ca/api/ai/threads';
};
